import {AsyncPipe} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatTable, MatTableDataSource, MatTableModule} from '@angular/material/table';
import {ActivatedRoute, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {BehaviorSubject} from 'rxjs';

import {selectActiveUser} from 'src/app/core/store/active-user/active-user.selector';
import {DeleteUserAction, GetAllUsersAction} from '../../core/store/users/users.action';
import {MessageService} from '../../services/message.service';
import {OphAvatarComponent} from '../../shared/design/oph-avatar/oph-avatar.component';
import {OphPaginatorComponent} from '../../shared/design/oph-paginator/oph-paginator.component';
import {ListViewsModule} from '../../shared/list-views/list-views.module';
import {ListViewWarningDialogComponent} from '../../shared/list-views/shared/warning-dialog/list-view-warning-dialog.component';
import {GetMenuOptionsByUserPipe} from '../pipes/get-menu-options-by-user.pipe';
import {User} from './../../core/model/user';
import {MobileService} from 'src/app/core/page/mobile.service';

@Component({
  selector: 'users-table',
  standalone: true,
  imports: [
    MatTableModule,
    OphAvatarComponent,
    ListViewsModule,
    OphPaginatorComponent,
    AsyncPipe,
    GetMenuOptionsByUserPipe,
  ],
  templateUrl: './users-table.component.html',
  styleUrl: './users-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersTableComponent implements OnInit, OnChanges, OnDestroy {
  private readonly mobileService = inject(MobileService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private store$ = inject(Store);
  private messageService = inject(MessageService);
  private dialog = inject(MatDialog);

  displayedColumns: string[] = ['name', 'username', 'team', 'role', 'lens', 'actions'];
  dataSource = new MatTableDataSource<User>([]);
  dataSource$ = new BehaviorSubject<User[]>([]);
  paginatedDataSource = signal<User[]>([]);
  @ViewChild('usersTableEl') usersTableEl: MatTable<User>;

  searchValue$ = input<BehaviorSubject<string>>();
  users = input.required<User[]>();

  pageSize = signal<number>(10);
  totalPages = signal<number>(0);
  currentPage = 0;
  currentUser$ = this.store$.select(selectActiveUser);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.users) {
      this.setUsers();
    }
  }

  ngOnInit() {
    this.setUsers();
    this.subscribeToDataSource();
    this.subscribeToSearchValue();
  }

  ngOnDestroy(): void {
    if (this.dataSource$) {
      this.dataSource$?.unsubscribe();
    }
  }

  setUsers(): void {
    this.updatePaginatedData();
  }

  subscribeToDataSource() {
    this.dataSource$.asObservable().subscribe(users => {
      this.dataSource.data = users;
    });
  }

  subscribeToSearchValue(): void {
    this.searchValue$().subscribe((searchValue: string) => {
      this.currentPage = 0;
      this.dataSource.data = this.users();
      this.dataSource.filter = searchValue.trim().toLowerCase();
      this.updatePaginatedData();
    });
  }

  updatePaginatedData() {
    // Get the total number of items based on filter
    const totalItems =
      this.dataSource.filter.length > 0 ? this.dataSource.filteredData.length : this.users()?.length || 0;

    // If we're on the last page and it has fewer items than pageSize, move to previous page
    if (this.currentPage > 0 && this.currentPage * this.pageSize() >= totalItems) {
      this.currentPage = Math.floor((totalItems - 1) / this.pageSize());
    }

    const startIndex = this.currentPage * this.pageSize();
    const endIndex = startIndex + this.pageSize();

    if (!this.dataSource.filter.length) {
      this.dataSource$.next(this.users().slice(startIndex, endIndex));
    } else {
      this.dataSource$.next(this.dataSource.filteredData.slice(startIndex, endIndex));
    }

    // Refresh the table
    if (this.usersTableEl) {
      this.usersTableEl.renderRows();
    }
    this.getTotalPages();
  }

  getTotalPages(): void {
    if (this.dataSource.filter.length > 0) {
      const totalPages = Math.ceil(this.dataSource.filteredData.length / this.pageSize());
      this.totalPages.set(totalPages);
    } else {
      this.totalPages.set(Math.ceil(this.users().length / this.pageSize()));
    }
  }

  onPageChange(page: number) {
    this.currentPage = page - 1;
    this.updatePaginatedData();
  }

  trackByFn(index: number, user: User) {
    if (!user?._id) return index;
    return user?._id;
  }

  onMenu(type: 'edit' | 'delete', user: User) {
    if (type === 'edit') {
      this.editUser(user);
    }
    if (type === 'delete') {
      this.deleteUser(user);
    }
  }

  editUser(user: User): void {
    const id = user?._id;
    this.router.navigate([id], {relativeTo: this.activatedRoute});
  }

  deleteUser(user: User) {
    const dialog = this.dialog.open(ListViewWarningDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      backdropClass: 'oph-backdrop',
      panelClass: 'list-views-warning-dialog',
      data: {type: 'user', user},
    });
    dialog.afterClosed().subscribe(confirm => {
      if (confirm) {
        this.store$.dispatch(
          new DeleteUserAction({
            userId: user._id,
            onSuccess: () => {
              this.store$.dispatch(new GetAllUsersAction({}));
            },
            onFailure: err => this.messageService.add(err, 'error'),
          })
        );
      }
    });
  }
}
